/**
 * SFMC Identity Store
 *
 * Remembers the visitor's contact details after any successful form submit so
 * later events (notably Browse Abandon) have a ContactKey to send to SFMC.
 *
 * SFMC requires an email as the ContactKey. Most product-page traffic is
 * anonymous, so without this store Browse Abandon could only ever fire for
 * logged-in customers.
 *
 * Every method is failure-tolerant: localStorage throws in Safari private mode
 * and when storage is full or disabled, and a marketing helper must never be
 * able to break a form submission.
 *
 * The whole file is guarded because more than one snippet can request it on
 * the same page (the product page loads it for both Browse Abandon and the
 * enquiry forwarder). Classic scripts share one global scope and each tag
 * executes, so an unguarded top-level `const` would throw a redeclaration
 * SyntaxError on the second tag. The guard keeps the declarations block-scoped
 * and makes a repeat load a no-op.
 */

if (!window.ZdSfmcIdentity) {
  const SFMC_IDENTITY_KEY = 'zd:sfmc:identity';

  const readSfmcIdentity = () => {
    try {
      const raw = window.localStorage.getItem(SFMC_IDENTITY_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      return parsed?.email ? parsed : null;
    } catch (error) {
      /* Storage unavailable or corrupt entry — treat the visitor as anonymous. */
      return null;
    }
  };

  const saveSfmcIdentity = (identity) => {
    try {
      const email = identity?.email?.trim();
      if (!email) return;

      const existing = readSfmcIdentity() ?? {};
      const merged = { ...existing };

      /* Only overwrite with values we actually have, so a later form that
         collects fewer fields cannot blank out details captured earlier. */
      merged.email = email;
      if (identity.name?.trim()) merged.name = identity.name.trim();
      if (identity.phone?.trim()) merged.phone = identity.phone.trim();
      merged.updatedAt = new Date().toISOString();

      window.localStorage.setItem(SFMC_IDENTITY_KEY, JSON.stringify(merged));
    } catch (error) {
      /* Non-fatal by design. */
    }
  };

  const clearSfmcIdentity = () => {
    try {
      window.localStorage.removeItem(SFMC_IDENTITY_KEY);
    } catch (error) {
      /* Non-fatal by design. */
    }
  };

  window.ZdSfmcIdentity = {
    key: SFMC_IDENTITY_KEY,
    read: readSfmcIdentity,
    save: saveSfmcIdentity,
    clear: clearSfmcIdentity,
  };
}
